
import { Component, Prop, Watch, Vue } from 'vue-property-decorator'

import { DotationOrderListItem } from '../types/dotationOrderList'

type PaymentMethod = 'card' | 'transfer' | 'acompte'

@Component({ name: 'OrderListCard' })
export default class extends Vue {
  @Prop({ required: true }) order!: DotationOrderListItem
  @Prop({ default: false }) expanded!: boolean
  @Prop({ default: false }) paying!: boolean
  @Prop({ default: false }) rescheduling!: boolean
  @Prop({ default: 0 }) availableBudget!: number
  @Prop({ default: 0 }) reservedAmount!: number

  paymentMethod: PaymentMethod | null = null
  showInsufficientAlert = false
  rescheduleDate: Date | null = null

  get isPaid(): boolean {
    return this.order.status === 'paid'
  }

  get canPay(): boolean {
    return this.order.status !== 'paid' && this.order.beneficiaries.length > 0
  }

  // the order's own scheduledAt, only if it's in the past
  get expiredScheduledAt(): string | null {
    const scheduledAt = this.order.scheduledAt
    if (!scheduledAt) return null

    const today = new Date().toISOString().slice(0, 10)
    return scheduledAt.slice(0, 10) < today ? scheduledAt : null
  }

  get hasExpiredSchedule(): boolean {
    return this.canPay && !!this.expiredScheduledAt
  }

  get statusTag(): { type: string; label: string } {
    if (this.isPaid) {
      return { type: 'success', label: this.$t('dotationOrders.status.paid') as string }
    }
    return { type: 'warning', label: this.$t('dotationOrders.status.unpaid') as string }
  }

  get canDelete(): boolean {
    return this.order.status === 'draft'
  }

  get totalAvailable(): number {
    return this.availableBudget + this.reservedAmount
  }

  get acompteAvailable(): boolean {
    return this.totalAvailable > 0
  }

  get isZeroBalance(): boolean {
    return !this.acompteAvailable
  }

  get acompteSufficient(): boolean {
    return this.totalAvailable >= this.order.amount
  }

  get difference(): number {
    return Math.max(this.order.amount - this.totalAvailable, 0)
  }

  get acompteHint(): string {
    return this.$t('dotationOrders.paymentMethod.acompteHint', { amount: this.formatCurrency(this.availableBudget) }) as string
  }

  get acompteReservedHint(): string {
    return this.$t('dotationOrders.paymentMethod.acompteReservedHint', { amount: this.formatCurrency(this.reservedAmount) }) as string
  }

  @Watch('expanded', { immediate: true })
  onExpandedChange(expanded: boolean): void {
    if (!expanded) {
      this.paymentMethod = null
      this.showInsufficientAlert = false
      this.rescheduleDate = null
      return
    }
    if (!this.canPay) return
    if (this.acompteSufficient || this.isZeroBalance) this.paymentMethod = 'acompte'
  }

  onConfirmPayment(): void {
    if (this.paymentMethod === 'transfer') {
      this.goToTransfer(this.order.amount)
      return
    }

    if (this.paymentMethod === 'acompte') {
      if (this.acompteSufficient) {
        this.showInsufficientAlert = false
        this.$emit('pay')
      } else {
        this.showInsufficientAlert = true
      }
    }
  }

  goToTransfer(amount: number): void {
    this.$router.push({
      path: '/gifts/new-acompte',
      query: { amount: String(amount), orderId: this.order.id, orderReference: this.order.reference }
    })
  }

  formatCurrency(amount: number): string {
    return this.$n(amount || 0, 'currency') as string
  }

  formatDate(value?: string | null): string {
    if (!value) return '—'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleDateString('fr-FR')
  }

  applyRescheduleDate(): void {
    if (!this.rescheduleDate) return

    const date = this.rescheduleDate
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    this.$emit('reschedule', `${year}-${month}-${day}`)
  }
}
